'use strict';

/**
 * Antavo loyalty client-side wiring.
 *
 * The vendor cartridge ships its client logic as full reimplementations of the
 * base cart/checkout files; importing those wholesale would shadow this
 * project's customizations, so this module extracts only the Antavo-specific
 * behavior as processInclude-d setup methods.
 *
 * Anything that participates in the checkout state machine lives in
 * js/checkout.js instead — each webpack entry is a separate bundle with its own
 * module cache, so wraps made here would be invisible to the state machine.
 */

var toast = require('core/components/toast');

/**
 * PayPal and Affirm cannot combine with the LOYALTY_PAY tender: hide their
 * billing tabs and panes while points are applied. Elements the server already
 * suppressed at render are absent from the DOM, so restoring is a no-op there.
 * @param {boolean} suppressed - true while points are applied
 */
function setAlternateTendersSuppressed(suppressed) {
    var $tabs = $('.payment-options .nav-item[data-method-id="PayPal"], .payment-options .nav-item[data-method-id="Affirm"]');
    $tabs.toggleClass('d-none', suppressed);
    $('#paypal-content, #affirm-payment-content').toggleClass('d-none', suppressed);

    // If the suppressed tab was active, fall back to the credit card tab so the
    // active pane (which supplies dwfrm_billing_paymentMethod) stays consistent.
    if (suppressed && $tabs.find('.nav-link.active').length) {
        $('.payment-options .nav-item[data-method-id="CREDIT_CARD"] .nav-link').trigger('click');
    }
}

/**
 * Format a points value as the currency amount it redeems for. Preview-only:
 * applied-state money always comes back server-formatted.
 * @param {jQuery} $form - the .loyalty-form element
 * @param {number} points - points to convert
 * @returns {string} formatted currency amount
 */
function formatPointsValue($form, points) {
    var burnPoints = parseFloat($form.attr('data-burn-points'));
    var burnAmount = parseFloat($form.attr('data-burn-amount'));
    var amount = burnPoints > 0 ? points * (burnAmount / burnPoints) : 0;
    return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: $form.attr('data-currency-code')
    }).format(amount);
}

/**
 * Recompute data-redeem-all-points, the redemption ceiling in points:
 * min(spendable balance, points needed to cover the redeemable amount). The
 * server renders the initial value, but the basket can move afterwards
 * (shipping method, tax, promotions), so recompute on every broadcast. The
 * basis is the merchandise subtotal, not the gross total, so points never
 * stretch over tax and shipping; LOYALTY_PAY is a tender and does not reduce
 * it, so the ceiling is the same whether or not points are applied.
 * @param {jQuery} $form - the .loyalty-form element
 * @param {number} redeemableAmount - order totals.antavoRedeemableAmount
 */
function syncRedeemAll($form, redeemableAmount) {
    var burnPoints = parseFloat($form.attr('data-burn-points'));
    var burnAmount = parseFloat($form.attr('data-burn-amount'));
    var spendable = parseInt($form.attr('data-spendable'), 10) || 0;
    if (!(redeemableAmount > 0) || !(burnAmount > 0) || !(burnPoints > 0)) {
        return;
    }
    var requiredPoints = Math.ceil((redeemableAmount / burnAmount) * burnPoints);
    $form.attr('data-redeem-all-points', Math.min(spendable, requiredPoints));
}

/**
 * Read the manual input once and derive every figure the control needs. The
 * posted value is clamped to the spendable balance because the route errors —
 * not clamps — past it; the redemption ceiling bounds the preview.
 * @param {jQuery} $form - the .loyalty-form element
 * @returns {Object} typed points, spendable balance, postPoints
 *   (balance-clamped value safe to POST), previewPoints (fully clamped value
 *   to preview), and the overBalance / overCeiling flags
 */
function getInputState($form) {
    var typed = parseInt($form.find('.antavo-points-input').val(), 10) || 0;
    var spendable = parseInt($form.attr('data-spendable'), 10) || 0;
    var redeemAllPoints = parseInt($form.attr('data-redeem-all-points'), 10) || 0;
    return {
        typed: typed,
        spendable: spendable,
        postPoints: Math.min(typed, spendable),
        previewPoints: Math.min(typed, redeemAllPoints),
        overBalance: typed > spendable,
        overCeiling: typed > redeemAllPoints
    };
}

/**
 * Sync the default body with what's typed: enable Apply only for a usable
 * amount, show the live discount preview, and explain whichever cap the typed
 * amount ran into. One note element carries both messages; the balance note
 * wins because it is the more fundamental limit.
 * @param {jQuery} $form - the .loyalty-form element
 */
function updatePreview($form) {
    var state = getInputState($form);
    var hasInput = $form.find('.antavo-points-input').val() !== '';
    var applied = $('.antavo-payment-form').attr('data-applied') === 'true';

    $form.find('.antavo-redeem-btn').prop('disabled', state.previewPoints < 1);

    var showPreview = hasInput && state.previewPoints >= 1 && !applied;
    $form.find('.antavo-redeem-preview')
        .toggleClass('d-none', !showPreview)
        .text(showPreview
            ? $form.attr('data-msg-discount').replace('{0}', formatPointsValue($form, state.previewPoints))
            : '');

    var note = '';
    if (state.overBalance) {
        note = $form.attr('data-msg-clamp')
            .replace('{0}', state.spendable)
            .replace('{1}', state.previewPoints);
    } else if (state.overCeiling && state.previewPoints >= 1) {
        note = $form.attr('data-msg-cap').replace('{0}', state.previewPoints);
    }

    $form.find('.antavo-redeem-clamp-note')
        .toggleClass('d-none', note === '')
        .text(note);
}

/**
 * Flip the control between its default (input) and applied (success bar)
 * bodies. data-applied is the single state flag the rest of the module keys off.
 * @param {boolean} applied - true to show the applied body
 */
function setRedemptionBody(applied) {
    $('.antavo-payment-form').attr('data-applied', applied ? 'true' : 'false');
    $('.antavo-redeem-default').toggleClass('d-none', applied);
    $('.antavo-redeem-applied').toggleClass('d-none', !applied);
}

/**
 * Write an apply result into the applied body.
 * @param {jQuery} $form - the .loyalty-form element
 * @param {number} applied - points the server actually redeemed
 * @param {string} discount - server-formatted discount amount
 */
function fillAppliedState($form, applied, discount) {
    $form.find('.antavo-applied-points-value').text(applied);
    $form.find('.antavo-applied-discount').text($form.attr('data-msg-discount').replace('{0}', discount));
}

/**
 * Reset the loyalty-owned checkout UI to the no-redemption state. This module
 * toggles only what no checkout listener manages; everything else (totals,
 * payment regions, billing fields, summaries) belongs to the
 * checkout:updateCheckoutView listeners, so both callers pair this with that
 * broadcast.
 */
function resetRedemptionUI() {
    var $form = $('.loyalty-form');
    setRedemptionBody(false);
    $form.find('.antavo-points-input').val('');
    $form.find('.antavo-redeem-btn').prop('disabled', true);
    $form.find('.antavo-redeem-preview, .antavo-redeem-clamp-note').addClass('d-none').text('');
    $form.find('.antavo-applied-points-value, .antavo-applied-discount').text('');
    $('.order-total-summary .antavo-item').addClass('d-none');

    // Revert the loyalty-owned pieces of the full-coverage UX.
    $('.payment-options-block').removeClass('d-none');
    $('.antavo-submit-payment').addClass('d-none').attr('data-loyalty-enabled', 'false');
    $('.submit-payment').removeClass('d-none');

    setAlternateTendersSuppressed(false);
}

/**
 * POST the loyalty form to Antavo-SubmitPayment with an explicit points value.
 * The form is serialized so the CSRF token rides along; the points field is
 * overridden because Apply Maximum posts the full spendable balance regardless
 * of what the input holds.
 * @param {jQuery} $form - the .loyalty-form element
 * @param {number} points - points to redeem
 */
function applyPoints($form, points) {
    $.spinner().start();
    var pointsFieldName = $form.find('.antavo-points-input').attr('name');
    var params = $form.serializeArray().map(function (param) {
        return param.name === pointsFieldName ? { name: param.name, value: points } : param;
    });

    $.ajax({
        url: $form.attr('action'),
        type: 'POST',
        dataType: 'json',
        data: $.param(params),
        success: function (data) {
            $.spinner().stop();
            $form.find('.invalid-feedback').hide().text('');

            if (!data.resultObj || data.resultObj.error) {
                $form.find('.invalid-feedback')
                    .text((data.resultObj && data.resultObj.message) || '').show();
                return;
            }

            var applied = data.resultObj.totalPointsSpent;
            var discount = data.order && data.order.totals ? data.order.totals.burnedAntavoPoints : '';
            setAlternateTendersSuppressed(true);

            // The route may have applied fewer points than posted; the ceiling
            // self-heals via loyaltyRedeemAllSync off the broadcast below.

            fillAppliedState($form, applied, discount);
            setRedemptionBody(true);
            $form.find('.antavo-redeem-preview, .antavo-redeem-clamp-note').addClass('d-none').text('');

            // Loyalty-owned summary row (no checkout listener manages it).
            $('.order-total-summary .antavo-item .antavo-total').text('-' + discount);
            $('.order-total-summary .antavo-item').removeClass('d-none');

            // Full coverage: no payment is required, so hide the payment
            // options and swap the submit buttons. The swapped button's click
            // flows through the state machine into the handlePaymentStage wrap
            // (js/checkout.js), which branches on data-loyalty-enabled and
            // submits paymentMethod=LOYALTY_PAY.
            if (data.resultObj.antavoPointsEnough) {
                $('.payment-options-block').addClass('d-none');
                $('.submit-payment').addClass('d-none');
                $('.antavo-submit-payment').removeClass('d-none');
                // data-loyalty-enabled renders server-side; keep it in sync
                // with the live loyalty state.
                $('.antavo-submit-payment').attr('data-loyalty-enabled', 'true');
            } else {
                $('.payment-options-block').removeClass('d-none');
                $('.antavo-submit-payment').addClass('d-none').attr('data-loyalty-enabled', 'false');
                $('.submit-payment').removeClass('d-none');
            }

            // Applying points is a basket mutation like any other: broadcast
            // the canonical resync event so the checkout listeners (totals,
            // zero-due payment regions, billing info) update themselves —
            // hand-patching subsets of that UI here drifts out of sync.
            $('body').trigger('checkout:updateCheckoutView', {
                order: data.order,
                customer: data.customer
            });
        },
        error: function () {
            $.spinner().stop();
        }
    });
}

/**
 * POST to Antavo-RemovePoints and reset the control and checkout UI to the
 * no-redemption state.
 * @param {jQuery} $form - the .loyalty-form element
 */
function removePoints($form) {
    $.spinner().start();

    $.ajax({
        url: $form.attr('data-remove-url'),
        type: 'POST',
        dataType: 'json',
        data: {},
        success: function (data) {
            $.spinner().stop();
            $form.find('.invalid-feedback').hide().text('');
            if (data.error) {
                $form.find('.invalid-feedback')
                    .text(data.message || '').show();
                return;
            }

            resetRedemptionUI();

            // Broadcast the canonical resync event (see the applyPoints note);
            // without it the zero-due listeners never hear about the removal
            // and the billing-address fields stay hidden.
            $('body').trigger('checkout:updateCheckoutView', {
                order: data.order,
                customer: data.customer
            });
        },
        error: function () {
            $.spinner().stop();
        }
    });
}

module.exports = {
    /**
     * Refresh the cart-page earn-points value on every cart:update, which
     * fires with the fresh cart model after every cart mutation. Only the
     * number inside the server-rendered totals row is updated, leaving the
     * enrollment-aware message untouched.
     */
    cartPoints: function () {
        $('body').on('cart:update', function (e, data) {
            if (!data || !data.antavoRewardPoints) return;
            var points = data.antavoRewardPoints.points;
            if (points === undefined || points === null) return;
            // The localized unit rides in data-points-unit so no copy is
            // hard-coded here.
            var $value = $('.antavo-cart-points .antavo-points-value');
            $value.text(points + ' ' + ($value.attr('data-points-unit') || ''));
        });
    },

    /**
     * Explicit Rewards Points redemption. Typing only updates the preview;
     * nothing commits until Apply Points (the form's submit, so Enter works
     * too) or Apply Maximum is clicked. The posted value is clamped to the
     * member's balance client-side because the route errors — not clamps —
     * past it; the route caps to the merchandise subtotal, which is why Apply
     * Maximum can safely post the whole balance.
     */
    loyaltySubmit: function () {
        $(document).on('input', '.loyalty-form .antavo-points-input', function () {
            // Whole positive numbers only — strip anything parseInt could
            // misread ("-", ".", "e", pasted text).
            var digits = this.value.replace(/\D/g, '');
            if (this.value !== digits) {
                this.value = digits;
            }
            updatePreview($(this).closest('.loyalty-form'));
        });

        $(document).on('submit', '.loyalty-form', function (e) {
            e.preventDefault();
            var $form = $(this);
            var state = getInputState($form);
            // Enter submits the form even while the Apply button is disabled.
            if (state.postPoints >= 1) {
                applyPoints($form, state.postPoints);
            }
        });

        $(document).on('click', '.antavo-redeem-all-btn', function () {
            var $form = $(this).closest('.loyalty-form');
            applyPoints($form, parseInt($form.attr('data-spendable'), 10) || 0);
        });
    },

    /**
     * The applied body's Remove action (adjusting an amount is remove + redeem
     * again). The legacy .remove-points-spent selector stays wired in case
     * another surface still renders it.
     */
    loyaltyRemove: function () {
        $(document).on('click', '.antavo-remove-points, .remove-points-spent', function (e) {
            e.preventDefault();
            removePoints($('.loyalty-form'));
        });
    },

    /**
     * The server strips the LOYALTY_PAY instrument whenever a basket change
     * alters the total (e.g. a shipping edit changes taxes). If a broadcast
     * order model no longer carries LOYALTY_PAY while the control still shows
     * an applied state, reset it and restore the payment UI.
     */
    loyaltyBasketSync: function () {
        $('body').on('checkout:updateCheckoutView', function (e, data) {
            var $control = $('.antavo-payment-form');
            if (!$control.length
                || $control.attr('data-applied') !== 'true'
                || !data || !data.order || !data.order.billing || !data.order.billing.payment) {
                return;
            }
            var instruments = data.order.billing.payment.selectedPaymentInstruments || [];
            var hasLoyaltyPay = instruments.some(function (instrument) {
                return instrument.paymentMethod === 'LOYALTY_PAY';
            });
            if (!hasLoyaltyPay) {
                resetRedemptionUI();
            }
        });
    },

    /**
     * The redemption ceiling moves when the basket moves (shipping method,
     * tax, promotions, cart edits). Recompute it from every broadcast order
     * model so the control never lets more points on than the route accepts.
     */
    loyaltyRedeemAllSync: function () {
        $('body').on('checkout:updateCheckoutView', function (e, data) {
            var $form = $('.loyalty-form');
            if (!$form.length || !data || !data.order || !data.order.totals) {
                return;
            }
            syncRedeemAll($form, data.order.totals.antavoRedeemableAmount);
        });
    },

    /**
     * The checkout points-to-earn card is server-rendered once, but its earn
     * basis is net of applied points, so the number has to fall as points go
     * on and recover when they come off; totals.antavoEarnPoints carries the
     * recomputed value on every broadcast. A null or zero value hides the whole
     * card rather than showing "0 Points", matching the server render. Only
     * the value span is rewritten so the enrollment-aware message and tooltip
     * are left alone.
     */
    checkoutPointsSync: function () {
        $('body').on('checkout:updateCheckoutView', function (e, data) {
            var $card = $('.antavo-checkout-points');
            if (!$card.length || !data || !data.order || !data.order.totals) {
                return;
            }

            var points = data.order.totals.antavoEarnPoints;
            var earns = points !== undefined && points !== null && points > 0;
            $card.toggleClass('d-none', !earns);

            if (earns) {
                var $value = $card.find('.antavo-points-value');
                $value.text(points + ' ' + ($value.attr('data-points-unit') || ''));
            }
        });
    },

    /**
     * Hide the whole Payment card while applied points cover the entire
     * basket — no payment is collected in that state. The server renders the
     * initial state; this keys the same rule off every broadcast. Digital-only
     * orders are exempt: they skip the shipping address, so the billing block
     * is the only source of the member's name and phone. The has-LOYALTY_PAY
     * condition keeps the gift-certificate zero-due flow untouched.
     */
    fullCoveragePaymentSection: function () {
        $('body').on('checkout:updateCheckoutView', function (e, data) {
            var $card = $('.card.payment-form');
            if (!$card.length
                || $card.attr('data-digital-only') === 'true'
                || !data || !data.order || !data.order.totals
                || !data.order.billing || !data.order.billing.payment) {
                return;
            }
            var instruments = data.order.billing.payment.selectedPaymentInstruments || [];
            var hasLoyaltyPay = instruments.some(function (instrument) {
                return instrument.paymentMethod === 'LOYALTY_PAY';
            });
            var isFullCoverage = hasLoyaltyPay
                && !data.order.totals.grandTotalLessGiftCertificatePaymentInstrumentsValue;
            $card.toggleClass('d-none', isFullCoverage);
        });
    },

    /**
     * Modular "Join Backstage" enrollment button: delegated handler for any
     * element carrying [data-component-loyalty-enroll] (Page Designer button
     * or hand-authored content asset). The route returns a redirectUrl for
     * both outcomes: an authenticated customer is enrolled, a guest is sent
     * to registration to complete joining from there.
     *
     * Authored content links straight to the route instead of carrying the
     * attribute, so whoever edits the page sees a real target in the link
     * dialog rather than a bare "#". Those links are adopted here. The route
     * is POST-only, so a prefetch or a no-JS click cannot enroll anyone.
     */
    loyaltyEnroll: function () {
        $('a[href*="Account-OptIn"]').attr('data-component-loyalty-enroll', '');

        $('body').on('click', '[data-component-loyalty-enroll]', function (e) {
            e.preventDefault();
            var $btn = $(this);
            var url = $btn.attr('data-url')
                || (window.CachedData && window.CachedData.loyaltyEnrollUrl);
            // Often an authored link rather than a button, where the disabled
            // property does nothing, so in-flight state is tracked by hand.
            if (!url || $btn.data('loyaltyEnrollBusy')) return;

            $.spinner().start();
            $btn.data('loyaltyEnrollBusy', true).prop('disabled', true);

            $.ajax({
                url: url,
                method: 'POST',
                dataType: 'json',
                // Enrollment lands back on the page the button was clicked
                // from, so the content around it re-renders for a member.
                data: { returnUrl: $btn.attr('data-return-url')
                    || (window.location.pathname + window.location.search) },
                success: function (data) {
                    $.spinner().stop();
                    // Guest with the login flyout enabled: open it in place
                    // instead of navigating to the full login page.
                    if (data && data.requiresLogin && data.flyoutUrl) {
                        $btn.data('loyaltyEnrollBusy', false).prop('disabled', false);
                        $('body').trigger('initFlyout', { url: data.flyoutUrl });
                        return;
                    }
                    if (data && data.redirectUrl) {
                        window.location.href = data.redirectUrl;
                        return;
                    }
                    $btn.data('loyaltyEnrollBusy', false).prop('disabled', false);
                },
                error: function () {
                    $.spinner().stop();
                    $btn.data('loyaltyEnrollBusy', false).prop('disabled', false);
                    toast.methods.show('danger', 'We could not complete your request. Please try again.');
                }
            });
        });
    },

    /**
     * Modular "Leave Backstage" opt-out button. The button lives on cached
     * content pages, so user state is checked fresh on click: the GET returns
     * the opt-out modal HTML for an enrolled member, or a JSON message for a
     * guest / signed-in non-member.
     */
    loyaltyUnenroll: function () {
        $('body').on('click', '[data-component-loyalty-unenroll]', function (e) {
            e.preventDefault();
            var $btn = $(this);
            var url = $btn.attr('data-url');
            if (!url) return;

            $.spinner().start();

            $.ajax({
                url: url,
                method: 'GET',
                success: function (data) {
                    $.spinner().stop();
                    // JSON means guest or signed-in non-member: info toast only.
                    if (typeof data === 'object') {
                        if (data && data.message) {
                            toast.methods.show('info', data.message);
                        }
                        return;
                    }
                    // HTML means the opt-out modal: inject into a body container and open it.
                    var $container = $('#loyalty-optout-modal-container');
                    if (!$container.length) {
                        $container = $('<div id="loyalty-optout-modal-container"></div>').appendTo('body');
                    }
                    $container.html(data);
                    // Klaviyo's promo bar sits at z-index 90000, above Bootstrap's
                    // default backdrop. Tag this modal's backdrop so _integrations.scss
                    // can lift it above the Klaviyo layer; Bootstrap appends the
                    // backdrop node on show, so it can only be reached once shown.
                    $('#optOutModal')
                        .one('shown.bs.modal', function () {
                            $('.modal-backdrop').addClass('optout-modal-backdrop');
                        })
                        .modal('show');
                },
                error: function () {
                    $.spinner().stop();
                    toast.methods.show('danger', 'We could not complete your request. Please try again.');
                }
            });
        });

        $('body').on('submit', 'form.optout', function (e) {
            e.preventDefault();
            var $form = $(this);
            var $feedback = $form.find('.invalid-feedback');
            $feedback.hide().text('');
            $.spinner().start();

            $.ajax({
                url: $form.attr('action'),
                method: 'POST',
                dataType: 'json',
                data: $form.serialize(),
                success: function (data) {
                    $.spinner().stop();
                    if (data && data.success && data.redirectUrl) {
                        window.location.href = data.redirectUrl;
                        return;
                    }
                    $feedback.text((data && data.message) || 'We could not complete your request. Please try again.').show();
                },
                error: function () {
                    $.spinner().stop();
                    $feedback.text('We could not complete your request. Please try again.').show();
                }
            });
        });
    },

    /**
     * Load more of the activity history in place. The rows arrive as
     * server-rendered HTML so no date or points formatting is reimplemented in
     * the browser.
     *
     * data-offset is a raw event cursor, not a page number: the feed filters
     * out events belonging to the second points account, so a page of rows can
     * consume any number of raw events. The server reports where it stopped
     * and that value is written back onto the button for the next click. The
     * control removes itself when the feed reports no more.
     */
    loyaltyActivityLoadMore: function () {
        $('body').on('click', '[data-component-loyalty-activity-more]', function (e) {
            e.preventDefault();
            var $btn = $(this);
            var url = $btn.attr('data-url');
            if (!url) return;

            var $rows = $btn.closest('.backstage-activity').find('[data-loyalty-activity-rows]');
            if (!$rows.length) return;

            $.spinner().start();
            $btn.prop('disabled', true);

            $.ajax({
                url: url,
                method: 'GET',
                dataType: 'json',
                data: {
                    offset: $btn.attr('data-offset') || 0,
                    pageSize: $btn.attr('data-page-size') || 5,
                    // Echoed back so every page hides the same event types as
                    // the first one.
                    excludedActions: $btn.attr('data-excluded-actions') || ''
                },
                success: function (data) {
                    $.spinner().stop();

                    if (!data || data.error) {
                        // Leave what is on screen and stop offering more rather
                        // than showing an error over a readable list.
                        $btn.remove();
                        return;
                    }

                    if (data.html) {
                        $rows.append(data.html);
                    }

                    $btn.attr('data-offset', data.nextOffset);

                    if (data.hasMore) {
                        $btn.prop('disabled', false);
                    } else {
                        $btn.remove();
                    }
                },
                error: function () {
                    $.spinner().stop();
                    $btn.remove();
                }
            });
        });
    }
};
